import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AllInOneService } from 'src/app/shared/services/all-in-one.service';
import { KunyekService } from 'src/app/shared/services/kunyek.service';
import { MessageService } from 'src/app/shared/services/message.service';
import { IMyDpOptions } from 'mydatepicker';
import { KMemberSelectComponent } from 'src/app/shared/components/k-member-select/k-member-select.component';
import { MatDialog } from '@angular/material/dialog';


function numericStringValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const isValid = typeof value === 'string' && /^[0-9]*$/.test(value);
    return isValid ? null : { numericString: { value: control.value } };
  };
}

@Component({
  selector: 'app-gym-detail',
  templateUrl: './gym-detail.component.html',
  styleUrls: ['./gym-detail.component.scss']
})
export class GymDetailComponent implements OnInit {
  datePickerOptions: IMyDpOptions = {
    dateFormat: 'dd/mm/yyyy',
  };
  id!: string;
  profileImageSrc: string = '../../../assets/images/uploadphoto.png';
  gymForm!: FormGroup;
  isSaving: boolean = false;
  requiredMessage: string = 'This field must not be empty';
  saved = false;
  gettingNrcCode = false;
  nrcRegionCode: any = [];
  nrcTownshipCode: any = [];
  nrcType: any = [];
  regionList: any = [];
  filteredTowns: any[] = [];
  filteredWards: any[] = [];

  breadcrumbs: any[] = [
    { name: 'Gym', url: 'gym', isActive: true },
    { name: 'Gym Member', url: 'members/gymlist', isActive: true },
    { name: '', url: '', isActive: false }
  ];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private kunyek: KunyekService,
    private messageService: MessageService,
    private allInOneService: AllInOneService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe(value => this.id = value.get('id') || '');
    this.initializeForm();
    if (this.id) {
      this.getOneGymMember();
      this.getRegion();
    }
  }

  getOneGymMember() {
    this.kunyek.gymMember().get({ id: this.id }).subscribe({
      next: (res) => {
        const gymData = res.data;
        if (gymData) {
          this.gymForm.patchValue({
            member: gymData.name,
            syskey: gymData.syskey,
            name: gymData.name,
            fatherName: gymData.fathername,
            nrcregioncode: gymData.nrcregioncode,
            nrctownshipcode: gymData.nrctownshipcode,
            nrctype: gymData.nrctype,
            nrcnumber: gymData.nrcnumber,
            race: gymData.race,
            religion: gymData.religion,
            length: gymData.length.replace('cm', ''),
            weight: gymData.weight.replace('kg', ''),
            contactNo: gymData.phone,
            occupation: gymData.occupation,
            accountNo: gymData.accountno,
            accountNoFC: gymData.accountnofc.substring(2),
            cardCode: gymData.cardcode,
            voucherNo: gymData.voucherno,
            joinDate: this.allInOneService.changeStringToDateobject(gymData.joindate),
            lastPayDate: this.allInOneService.changeStringToDateobject(gymData.lastpaydate),
            memberStatus: gymData.memberstatus,
            statusDate: this.allInOneService.changeStringToDateobject(gymData?.memberstatusdate),
            address: gymData.address,
            remark: gymData.remark,
            region: gymData.directory?.region,
            town: gymData.directory?.town,
            ward: gymData.directory?.postcode
          });
          this.breadcrumbs[2].name = gymData.name;
          if (gymData.directory?.region && gymData.directory?.town) {
            this.onRegionChange({ target: { value: gymData?.directory?.region } });
            this.onTownChange({ target: { value: gymData?.directory?.town } });
          }
        }
      },
      error: (err) => {
        this.messageService.openSnackBar('Failed to fetch gym member details', 'warn');
      }
    });
  }

  getRegion() {
    const data = {};
    this.kunyek.getDirectory().get(data).subscribe(
      (res: any) => {
        if (res.returncode == '300') {
          this.regionList = res.datalist;
        } else {
          this.messageService.openSnackBar(res.message, 'warn');
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }

  onRegionChange(region: any) {
    const data = {
      region: region.target.value
    };
    this.kunyek.getDirectory().get(data).subscribe(
      (res: any) => {
        if (res.returncode == '300') {
          this.filteredTowns = res.datalist;
          this.filteredWards = [];
          this.resetDropdowns('town');
        } else {
          this.messageService.openSnackBar(res.message, 'warn');
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }

  resetDropdowns(type: string) {
    if (type === 'town') {
      this.filteredWards = [];
    }
  }

  onTownChange(town: any) {
    const data = {
      town: town.target.value
    };
    this.kunyek.getDirectory().get(data).subscribe(
      (res: any) => {
        if (res.returncode == '300') {
          this.filteredWards = res.datalist;
        } else {
          this.messageService.openSnackBar(res.message, 'warn');
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }

  initializeForm() {
    this.gymForm = this.fb.group({
      member: ['', Validators.required],
      syskey: ['', Validators.required],
      name: ['', Validators.required],
      fatherName: [''],
      nrcregioncode: ["", Validators.required],
      nrctownshipcode: ["", Validators.required],
      nrctype: ["", Validators.required],
      nrcnumber: ["", Validators.required],
      race: [''],
      religion: [''],
      length: [''],
      weight: [''],
      contactNo: ['', [Validators.required, numericStringValidator()]],
      occupation: [''],
      accountNo: [''],
      accountNoFC: [''],
      cardCode: [''],
      voucherNo: [''],
      joinDate: [''],
      lastPayDate: [''],
      memberStatus: ['', Validators.required],
      statusDate: [''],
      address: [''],
      remark: [''],
      region: ['',],
      town: ['',],
      ward: ['',]
    });
    this.getNrcCode();
  }

  get name() { return this.gymForm.get('name'); }
  get fatherName() { return this.gymForm.get('fatherName'); }
  get nrcNo() { return this.gymForm.get('nrcNo'); }
  get race() { return this.gymForm.get('race'); }
  get religion() { return this.gymForm.get('religion'); }
  get length() { return this.gymForm.get('length'); }
  get weight() { return this.gymForm.get('weight'); }
  get contactNo() { return this.gymForm.get('contactNo'); }
  get occupation() { return this.gymForm.get('occupation'); }
  get accountNo() { return this.gymForm.get('accountNo'); }
  get accountNoFC() { return this.gymForm.get('accountNoFC'); }
  get cardCode() { return this.gymForm.get('cardCode'); }
  get voucherNo() { return this.gymForm.get('voucherNo'); }
  get joinDate() { return this.gymForm.get('joinDate'); }
  get lastPayDate() { return this.gymForm.get('lastPayDate'); }
  get memberStatus() { return this.gymForm.get('memberStatus'); }
  get statusDate() { return this.gymForm.get('statusDate'); }
  get address() { return this.gymForm.get('address'); }
  get nrcregioncode() { return this.gymForm.get('nrcregioncode'); }
  get nrctownshipcode() { return this.gymForm.get('nrctownshipcode'); }
  get nrctype() { return this.gymForm.get('nrctype'); }
  get nrcnumber() { return this.gymForm.get('nrcnumber'); }
  get syskey() { return this.gymForm.get('syskey'); }
  get remark() { return this.gymForm.get('remark'); }
  get member() { return this.gymForm.get('member'); }
  get region() { return this.gymForm.get('region'); }
  get town() { return this.gymForm.get('town'); }
  get ward() { return this.gymForm.get('ward'); }

  getNrcCode() {
    this.gettingNrcCode = true;
    const data = {};
    this.kunyek.getNrcCode(data).subscribe(
      (res: any) => {
        if (res.returncode == '300') {
          this.nrcRegionCode = res.datalist.regioncode;
          this.nrcTownshipCode = res.datalist.shortcode;
          this.nrcType = res.datalist.nrctype;
          this.gettingNrcCode = false;
        } else {
          this.gettingNrcCode = false;
          this.messageService.openSnackBar(res.message, 'warn');
        }
      },
      (err) => {
        this.gettingNrcCode = false;
      }
    );
  }

  openMemberModal() {
    const dialog = this.dialog.open(KMemberSelectComponent, {
      height: '606px',
      width: '500px',
      closeOnNavigation: true,
      panelClass: ['user-profile-modal', 'mat-typography'],
      data: {
        field: 'member'
      },
    });
    dialog.afterClosed().subscribe((dialogResult: any) => {
      if (dialogResult) {
        const memberData = dialogResult.data[0]
        this.gymForm.patchValue({
          member: memberData.name,
          name: memberData.name,
          syskey: memberData.syskey,
          id: memberData.userid,
          fatherName: memberData.fathername,
          nrcregioncode: memberData.nrcregioncode,
          nrctownshipcode: memberData.nrctownshipcode,
          nrctype: memberData.nrctype,
          nrcnumber: memberData.nrcnumber,
          race: memberData.nationality,
          religion: memberData.religion,
          occupation: memberData.currentjob,
          address: memberData.address,
        });
        this.gymForm.get('member')?.enable();
      }
    });
  }

  checkAndOpenMemberModal() {
    this.openMemberModal();
  }

  onSave() {
    this.isSaving = true;
    this.saved = true;
    if (this.gymForm.valid) {
      const formattedJoinDate = this.joinDate?.value?.formatted || this.joinDate?.value;
      const formattedLastPayDate = this.lastPayDate?.value?.formatted || this.lastPayDate?.value;
      const formattedMemberStatusDate = this.statusDate?.value?.formatted || this.statusDate?.value;
      const formData = {
        syskey: this.syskey?.value,
        name: this.name?.value,
        fathername: this.fatherName?.value,
        nrcregioncode: this.nrcregioncode?.value,
        nrctype: this.nrctype?.value,
        nrctownshipcode: this.nrctownshipcode?.value,
        nrcnumber: this.nrcnumber?.value,
        race: this.race?.value,
        religion: this.religion?.value,
        length: this.length?.value + 'cm',
        weight: this.weight?.value + 'kg',
        contactno: this.contactNo?.value,
        occupation: this.occupation?.value,
        accountno: this.accountNo?.value,
        accountnofc: "FC" + this.accountNoFC?.value,
        cardcode: this.cardCode?.value,
        voucherno: this.voucherNo?.value,
        joindate: this.allInOneService.convertDate(formattedJoinDate),
        lastpaydate: this.allInOneService.convertDate(formattedLastPayDate),
        memberstatus: this.memberStatus?.value,
        memberstatusdate: this.allInOneService.convertDate(formattedMemberStatusDate),
        address: this.address?.value,
        nrc: this.nrcregioncode?.value + '/' + this.nrctownshipcode?.value +
          '(' + this.nrctype?.value + ')' +
          this.nrcnumber?.value,
        remark: this.remark?.value,
        postcode: this.ward?.value,
      };
      this.kunyek.gymMember().update(formData).subscribe({
        next: (res) => {
          this.isSaving = false;
          this.messageService.openSnackBar('Success!', 'success');
          this.closeForm()
        },
        error: (err) => {
          this.messageService.openSnackBar('Failed to save member', 'warn');
          this.isSaving = false;
        }
      });
    } else {
      this.messageService.openSnackBar('Invalid Form', 'warn');
      this.isSaving = false;
    }
  }

  onProfileClick() {
    const input = document.createElement('input');
    input.hidden = true;
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', (event: Event) => {
      this.onImageChange(event);
    });
    input.click();
  }

  private onImageChange(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => this.profileImageSrc = reader.result as string;
      reader.readAsDataURL(file);
    }
  }

  closeForm() {
    this.router.navigate(['members', 'gymlist']);
  }
}
